import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useForm } from 'react-hook-form';
import { AuthLayout } from '../components/auth';
import { FormField, Button } from '../components/forms';
import { getUserInfo } from '../services/userService';
import { searchFranchisees } from '../services/franchiseeSearchService';
import { useAdminView } from '../context/AdminViewContext';
import { FaSearch, FaTimes } from 'react-icons/fa';

const AdminLoginPage = () => {
  const [step, setStep] = useState('login'); // 'login' | 'select-franchisee'
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const navigate = useNavigate();
  const { startAdminView } = useAdminView();

  // Franchisee search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  useEffect(() => { setError(''); }, [watchedEmail, watchedPassword]);

  // Auto logout when coming to this page to ensure clean state
  useEffect(() => {
    return () => {};
  }, []);

  const handleLogin = async (data) => {
    setError('');
    setIsSubmitting(true);
    setIsLoginInProgress(true);

    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      const user = auth.currentUser;
      if (!user) throw new Error('Login failed.');

      const idToken = await user.getIdToken();
      const userInfo = await getUserInfo(idToken);

      if (userInfo.userType !== 'admin') {
        await auth.signOut();
        setError('Login not allowed: only admin users can log in.');
        setIsLoginInProgress(false);
        setIsSubmitting(false);
        return;
      }

      setAdminEmail(userInfo.email || data.email);
      setStep('select-franchisee');
    } catch (err) {
      console.error('Admin login error:', err);
      const msg = err.message || 'Login failed.';
      setError(
        msg.includes('auth/invalid-credential') || msg.includes('auth/invalid-email') || msg.includes('auth/user-not-found') || msg.includes('auth/wrong-password')
          ? 'Invalid email or password.'
          : msg.includes('auth/too-many-requests')
            ? 'Too many failed attempts. Please try again later.'
            : msg
      );
    } finally {
      setIsSubmitting(false);
      setIsLoginInProgress(false);
    }
  };

  const handleSearch = useCallback(async (value) => {
    setSearchTerm(value);
    if (!value || value.length < 3) {
      setSearchResults([]);
      setSearchError('');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const results = await searchFranchisees(value);
      setSearchResults(results);
      if (results.length === 0) setSearchError('No franchisees found.');
    } catch (err) {
      setSearchError(err.message || 'Search failed.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSelectFranchisee = (franchisee) => {
    const name = franchisee.franchiseName || `${franchisee.firstName || ''} ${franchisee.lastName || ''}`.trim() || 'Unknown';
    startAdminView(franchisee.id, name);
    navigate('/');
  };

  const handleBack = () => {
    setStep('login');
    setSearchTerm('');
    setSearchResults([]);
    setSearchError('');
    auth.signOut();
  };

  if (step === 'select-franchisee') {
    return (
      <div className="min-h-screen bg-sky-100 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Admin View - Select Franchisee</h2>
            <button
              onClick={handleBack}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Back to login
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Logged in as <span className="font-medium">{adminEmail}</span>. Search for a franchisee to view their account in read-only mode.
          </p>

          <div className="relative mb-4">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, franchise code, or email (min 3 chars)..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              autoFocus
            />
            {searchTerm && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {searching && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Searching...</p>
            </div>
          )}

          {searchError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-700">
              {searchError}
            </div>
          )}

          {searchTerm.length > 0 && searchTerm.length < 3 && !searching && (
            <p className="text-sm text-gray-500 text-center py-4">Please enter at least 3 characters to search.</p>
          )}

          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Code</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Franchise</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searchResults.map((f) => (
                    <tr key={f.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono">{f.fseCode || '-'}</td>
                      <td className="px-4 py-3">{f.firstName} {f.lastName}</td>
                      <td className="px-4 py-3 text-gray-600">{f.franchiseName || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleSelectFranchisee(f)}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <AuthLayout title="Admin Login" subtitle="Log in to view franchisee accounts" currentStep={1} totalSteps={1}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="admin@example.com"
          register={register}
          validation={{
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email format' }
          }}
          error={errors.email}
        />

        <FormField
          label="Password"
          name="password"
          type="password"
          placeholder="Enter your password"
          register={register}
          validation={{
            required: 'Password is required',
            minLength: { value: 6, message: 'Password must be at least 6 characters' }
          }}
          error={errors.password}
        />

        <Button type="submit" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Logging in...' : 'Log In'}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <a href="/login" className="text-sm text-gray-500 hover:text-gray-700 underline">
          Franchisee login
        </a>
      </div>
    </AuthLayout>
  );
};

export default AdminLoginPage;
